import { ReactNode } from 'react'
import type { TableNode } from '@table-library/react-table-library/types/table'
import { useTranslation } from 'libs/i18n'
import { ContextMenu } from 'shared/components'
import type { Option } from 'shared/components/ContextMenu/types'
import type { CardTableColumn } from 'styles/table'
import { DayFlag } from 'features/openingHours/molecules/DayFlag/DayFlag'
import { useWeekday } from 'features/openingHours/hooks/useWeekday'
import type { OpeningHourItem } from 'features/openingHours/types/openingHours.types'

type UseColumnsProps = {
	menuOptions: Option[]
}

const textClassName = 'block truncate text-[13px] text-muted-foreground'
const padding = '12px 16px'

const cell = (render: (openingHour: OpeningHourItem) => ReactNode) => (node: TableNode) =>
	render(node as unknown as OpeningHourItem)

export const useColumns = ({ menuOptions }: UseColumnsProps): CardTableColumn[] => {
	const { t } = useTranslation()
	const getWeekday = useWeekday()

	const flagColumn = (
		label: string,
		width: string,
		getValue: (openingHour: OpeningHourItem) => boolean
	): CardTableColumn => ({
		label,
		width,
		padding,
		renderCell: cell((item) => (
			<span className="block text-[13px]">
				<DayFlag value={getValue(item)} label={label} />
			</span>
		))
	})

	return [
		{
			label: t('Date'),
			width: 'minmax(110px, 1.55fr)',
			padding,
			renderCell: cell((item) => (
				<span className="block truncate text-[13px] font-semibold tabular-nums text-foreground">
					{item.date}
				</span>
			)),
			pinLeft: true
		},
		flagColumn(t('Open'), 'minmax(70px, 0.95fr)', (item) => item.is_open),
		flagColumn(t('Bookable'), 'minmax(90px, 1.35fr)', (item) => item.is_bookable),
		flagColumn(
			t('Counted in duration'),
			'minmax(150px, 2.35fr)',
			(item) => item.counts_towards_duration
		),
		{
			label: t('Weekday'),
			width: 'minmax(100px, 1.5fr)',
			padding,
			renderCell: cell((item) => <span className={textClassName}>{getWeekday(item.date)}</span>)
		},
		{
			label: t('Holiday'),
			width: 'minmax(120px, 1.85fr)',
			padding,
			renderCell: cell((item) => <span className={textClassName}>{item.is_holiday}</span>)
		},
		{
			label: t('Reason'),
			width: 'minmax(100px, 1.15fr)',
			padding,
			renderCell: cell((item) => <span className={textClassName}>{item.reason}</span>)
		},
		{
			label: '',
			width: '48px',
			padding: '12px 8px',
			renderCell: cell((item) => (
				<ContextMenu
					item={item}
					options={menuOptions}
					label={t('Actions for {{ name }}', { name: item.date })}
				/>
			))
		}
	]
}
